import React, { useRef, useEffect, useMemo, useCallback } from "react";
import { Container, Graphics, Text, Sprite } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import * as PIXI from "pixi.js";
import { TextMetrics } from "pixi.js";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  closeAutoplayPopup,
  setAutoSpinCount,
  startAutoPlay,
  setStopOnAnyWin,
  setStopOnBonusGame,
  setStopOnSingleWinExceeds,
  setStopOnBalanceIncreases,
  setStopOnBalanceDecreases,
  setSingleWinExceedsAmount,
  setBalanceIncreasesAmount,
  setBalanceDecreasesAmount,
} from "../../../../State/actions";
import {
  selectAutoplayPopupActive,
  selectBet,
  selectBalance,
  selectStopOnAnyWin,
  selectStopOnBonusGame,
  selectStopOnSingleWinExceeds,
  selectStopOnBalanceIncreases,
  selectStopOnBalanceDecreases,
  selectSingleWinExceedsAmount,
  selectBalanceIncreasesAmount,
  selectBalanceDecreasesAmount,
  selectCounterAnimationFinished,
  selectIsSpinning,
  selectCriticalAnimationPlaying,
} from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../textureConfigs";
import { ASSET_NAME } from "../../../../assets";
import ButtonMenuClose from "../Buttons/ButtonMenuClose";
import BetButton from "../Buttons/BetButton";
import Checkbox from "../Buttons/Checkbox";
import CheckboxWithInput from "../Buttons/CheckboxWithInput";
import anime from "animejs";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";

/**
 * PopiAutoplayPopup Component
 *
 * Displays a popup for configuring autoplay settings with two main sections:
 * 1. Stop Autospin conditions - checkboxes for stopping conditions (any win, bonus game,
 *    single win exceeds amount, balance increases/decreases by amount)
 * 2. Numbers of Rounds - grid of buttons to select autoplay count (10, 25, 50, etc.)
 *
 * Layout Modes:
 * - Mobile Landscape: Two-column layout (55% checkboxes, 45% buttons) side-by-side
 * - All Other Devices: Stacked layout (checkboxes on top, buttons below)
 * - Tablet Landscape/Desktop: Windowed mode with popup frame
 * - Mobile/Tablet Portrait: Fullscreen mode with dark background
 */

// Available autoplay round count options
const AUTOPLAY_NUMBERS = [10, 25, 50, 100, 250, 500, 750, 1000, Infinity];

/**
 * Responsive popup configuration
 * Base values are in pixels and scaled by uiSymbolScale (fullscreen) or popupScale (windowed)
 * Multipliers adjust sizing per device/orientation combination
 */
const POPUP_CONFIG = {
  // Base dimensions (scaled by device multipliers)
  basePadding: {
    horizontal: 36,
    vertical: 16,
  },
  baseTitleFontSize: 48,
  baseTitleGap: 48,
  baseSectionFontSize: 24,
  baseSectionGap: 20,

  // Device/orientation-specific multipliers
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 2.0, v: 7.0 },
      titleFontMultiplier: 1.75,
      titleGapMultiplier: 2.0,
      sectionFontMultiplier: 2.5,
      sectionGapMultiplier: 2.0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 4.5, v: 6.0 },
      titleFontMultiplier: 1.75,
      titleGapMultiplier: 1.5,
      sectionFontMultiplier: 2.5,
      sectionGapMultiplier: 2.0,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 8.0, v: 7.25 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.25,
      sectionFontMultiplier: 3.0,
      sectionGapMultiplier: 2.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 1.5 },
      titleFontMultiplier: 1.15,
      titleGapMultiplier: 1.25,
      sectionFontMultiplier: 2.0,
      sectionGapMultiplier: 1.5,
      // Windowed mode positioning offsets (as multipliers of popup size)
      positionOffsetRight: 0.95, // Offset from right edge
      positionOffsetBottom: 2.25, // Offset from bottom (in closeButtonConfig.height units)
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 1.5 },
      titleFontMultiplier: 1.0,
      titleGapMultiplier: 1.0,
      sectionFontMultiplier: 2.0,
      sectionGapMultiplier: 1.5,
      // Windowed mode positioning offsets (as multipliers of popup size)
      positionOffsetRight: 1.25, // Offset from right edge
      positionOffsetBottom: 2.25, // Offset from bottom (in closeButtonConfig.height units)
    },
  },
};

const AutoplayPopup = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // UI State
  const popupActive = useSelector(selectAutoplayPopupActive);
  const balance = useSelector(selectBalance);
  const bet = useSelector(selectBet);

  // Guards - prevent starting new spin if ongoing spin, win lines displaying, or critical animation
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const spinComplete = useSelector((state) => state.spin.spinComplete);
  const currentRound = useSelector((state) => state.game.currentRound);

  // Autoplay stop conditions state (responsible gambling features)
  const stopOnAnyWin = useSelector(selectStopOnAnyWin);
  const stopOnBonusGame = useSelector(selectStopOnBonusGame);
  const stopOnSingleWinExceeds = useSelector(selectStopOnSingleWinExceeds);
  const stopOnBalanceIncreases = useSelector(selectStopOnBalanceIncreases);
  const stopOnBalanceDecreases = useSelector(selectStopOnBalanceDecreases);
  const singleWinExceedsValue = useSelector(selectSingleWinExceedsAmount);
  const balanceIncreasesValue = useSelector(selectBalanceIncreasesAmount);
  const balanceDecreasesValue = useSelector(selectBalanceDecreasesAmount);

  // Responsive dimensions and device info
  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    getResponsiveConfig,
  } = useDimensions();

  // Refs for animation and DOM manipulation
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  // Input focus management (only one input can be focused at a time)
  const [focusedInput, setFocusedInput] = React.useState(null);

  // Basic/Advanced mode ref (only for windowed mode) - using ref to avoid re-render triggers
  const isAdvancedMode = useRef(false);

  // Force re-render when toggling mode - store state value for dependency tracking
  const [modeUpdateCounter, forceUpdate] = React.useReducer((x) => x + 1, 0);

  const handleClose = useCallback(() => {
    dispatch(closeAutoplayPopup());
  }, [dispatch]);

  // Determine display mode: windowed (with frame) for tablet landscape/desktop, fullscreen otherwise - memoized
  const showPopupFrame = useMemo(
    () =>
      (device === PixiSettings.DEVICE.TABLET &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE) ||
      device === PixiSettings.DEVICE.DESKTOP,
    [device, orientation]
  );

  // Toggle between Basic and Advanced mode
  const handleModeToggle = useCallback(() => {
    if (!showPopupFrame) return; // Only for windowed mode

    // Prevent toggle if ongoing spin, win lines displaying, or critical animation
    const hasWin = currentRound?.win && currentRound.win > 0;
    const winNotFullyDisplayed =
      hasWin && spinComplete && !counterAnimationFinished;

    if (isSpinning || winNotFullyDisplayed || criticalAnimationPlaying) {
      return;
    }

    soundManager.play(SOUND_NAME.button_click);

    // Toggle the mode and force re-render (instant, no animation)
    isAdvancedMode.current = !isAdvancedMode.current;
    forceUpdate();
  }, [
    showPopupFrame,
    forceUpdate,
    isSpinning,
    criticalAnimationPlaying,
    currentRound,
    spinComplete,
    counterAnimationFinished,
  ]);

  // Calculate button aspect ratio from texture for natural sizing - memoized
  const buttonBackgroundTexture = useMemo(
    () => PIXI.Assets.get(ASSET_NAME.button_bet_background),
    []
  );
  const buttonAspectRatio = useMemo(
    () =>
      buttonBackgroundTexture
        ? buttonBackgroundTexture.width / buttonBackgroundTexture.height
        : 2.0,
    [buttonBackgroundTexture]
  );

  // Triangle texture for Basic/Advanced toggle button - memoized
  const triangleTexture = useMemo(
    () => PIXI.Assets.get(ASSET_NAME.triangle),
    []
  );

  // Select appropriate popup frame texture based on display mode and Basic/Advanced state - memoized
  // Basic mode (default): Uses horizontal frame for windowed, Advanced uses vertical
  // Note: Uses modeUpdateCounter to ensure recalculation when mode changes via ref
  const popupFrameTexture = useMemo(() => {
    // Read ref value directly - modeUpdateCounter ensures recalculation on mode change
    const isAdvanced = isAdvancedMode.current;
    return PIXI.Assets.get(
      showPopupFrame
        ? isAdvanced
          ? ASSET_NAME.popup_frame_vertical
          : ASSET_NAME.popup_frame_horizontal
        : ASSET_NAME.popup_frame_settings
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopupFrame, modeUpdateCounter]);

  /**
   * Calculate popup dimensions and scale
   * Windowed mode: Uses texture size scaled by device-specific multiplier
   * Fullscreen mode: Uses full viewport dimensions
   */
  const popupSize = useMemo(() => {
    if (!popupFrameTexture) return { width: 0, height: 0, scale: 1 };

    if (showPopupFrame) {
      const sizeMultiplier =
        device === PixiSettings.DEVICE.DESKTOP ? 0.6 : 0.92;
      const finalScale = uiSymbolScale * sizeMultiplier;
      return {
        width: popupFrameTexture.width * finalScale,
        height: popupFrameTexture.height * finalScale,
        scale: finalScale,
      };
    }

    return {
      width: dimensions.width,
      height: dimensions.height,
      scale: uiSymbolScale,
    };
  }, [popupFrameTexture, showPopupFrame, dimensions, uiSymbolScale, device]);

  const popupScale = popupSize.scale;

  /**
   * Calculate viewport configuration for content area
   * Accounts for padding, title space, and device-specific multipliers
   * Returns null if textures aren't loaded yet
   */
  const viewportConfig = useMemo(() => {
    if (!popupFrameTexture || popupSize.width === 0) return null;

    const config = POPUP_CONFIG[device]?.[orientation] || {
      paddingMultiplier: { h: 1.0, v: 1.0 },
      titleFontMultiplier: 1.0,
      titleGapMultiplier: 1.0,
      sectionFontMultiplier: 1.0,
      sectionGapMultiplier: 1.0,
    };

    if (showPopupFrame) {
      // Windowed mode: scale with popupScale
      const padding =
        POPUP_CONFIG.basePadding.horizontal *
        config.paddingMultiplier.h *
        popupScale;
      const contentWidth = popupSize.width - padding * 2;
      const contentHeight = popupSize.height - padding * 2;
      const titleFontSize = Math.round(
        POPUP_CONFIG.baseTitleFontSize * config.titleFontMultiplier * popupScale
      );
      const titleGap =
        POPUP_CONFIG.baseTitleGap * config.titleGapMultiplier * popupScale;
      const titleYOffset = titleFontSize + titleGap;

      return {
        maxWidth: contentWidth,
        maxHeight: contentHeight,
        x: -popupSize.width / 2 + padding,
        y: -popupSize.height / 2 + padding,
        titleFontSize,
        titleYOffset,
        viewportHeight: contentHeight - titleYOffset,
        sectionFontSize: Math.round(
          POPUP_CONFIG.baseSectionFontSize *
            config.sectionFontMultiplier *
            popupScale
        ),
        sectionGap:
          POPUP_CONFIG.baseSectionGap *
          config.sectionGapMultiplier *
          popupScale,
      };
    }

    // Fullscreen mode: scale with uiSymbolScale
    const paddingH =
      POPUP_CONFIG.basePadding.horizontal *
      config.paddingMultiplier.h *
      uiSymbolScale;
    const paddingV =
      POPUP_CONFIG.basePadding.vertical *
      config.paddingMultiplier.v *
      uiSymbolScale;
    const contentWidth = dimensions.width - paddingH * 2;
    const contentHeight = dimensions.height - paddingV * 2;
    const titleFontSize = Math.round(
      POPUP_CONFIG.baseTitleFontSize *
        config.titleFontMultiplier *
        uiSymbolScale
    );
    const titleGap =
      POPUP_CONFIG.baseTitleGap * config.titleGapMultiplier * uiSymbolScale;
    const titleYOffset = titleFontSize + titleGap;

    return {
      maxWidth: contentWidth,
      maxHeight: contentHeight,
      x: -dimensions.width / 2 + paddingH,
      y: -dimensions.height / 2 + paddingV,
      titleFontSize,
      titleYOffset,
      viewportHeight: contentHeight - titleYOffset,
      sectionFontSize: Math.round(
        POPUP_CONFIG.baseSectionFontSize *
          config.sectionFontMultiplier *
          uiSymbolScale
      ),
      sectionGap:
        POPUP_CONFIG.baseSectionGap *
        config.sectionGapMultiplier *
        uiSymbolScale,
    };
  }, [
    popupSize,
    dimensions,
    popupFrameTexture,
    showPopupFrame,
    popupScale,
    uiSymbolScale,
    device,
    orientation,
  ]);

  // Memoize translation texts - prevents recalculation on every render
  const titleText = useMemo(() => t("ui_autospins_title"), [t]);
  const stopAutospinText = useMemo(() => t("ui_autospins_stop_autospin"), [t]);
  const numbersOfRoundsText = useMemo(
    () => t("ui_autospins_numbers_of_rounds"),
    [t]
  );
  const basicText = useMemo(() => t("ui_autospins_basic"), [t]);
  const advancedText = useMemo(() => t("ui_autospins_advanced"), [t]);

  // Auto-scaling title font size
  const finalTitleFontSize = useMemo(() => {
    if (!viewportConfig) return 48;

    const idealFontSize = viewportConfig.titleFontSize;
    const maxTextWidth = viewportConfig.maxWidth * 0.9; // 90% of content width

    // Measure text with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "500",
      align: "center",
    });
    const metrics = TextMetrics.measureText(titleText, tempStyle);

    // Scale down if text exceeds max width
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    return idealFontSize;
  }, [viewportConfig, titleText]);

  // Auto-scaling section heading font size
  const finalSectionFontSize = useMemo(() => {
    if (!viewportConfig) return 24;

    const longerText =
      stopAutospinText.length > numbersOfRoundsText.length
        ? stopAutospinText
        : numbersOfRoundsText;
    const idealFontSize = viewportConfig.sectionFontSize;
    const maxTextWidth = viewportConfig.maxWidth * 0.9; // 90% of content width

    // Measure text with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "400",
      align: "left",
    });
    const metrics = TextMetrics.measureText(longerText, tempStyle);

    // Scale down if text exceeds max width
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    return idealFontSize;
  }, [viewportConfig, stopAutospinText, numbersOfRoundsText]);

  // Text styles for title and section headings
  const titleStyle = useMemo(
    () =>
      viewportConfig &&
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalTitleFontSize,
        fontWeight: "500",
        fill: 0xffffff,
        align: "center",
      }),
    [viewportConfig, finalTitleFontSize]
  );

  const sectionHeadingStyle = useMemo(
    () =>
      viewportConfig &&
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalSectionFontSize,
        fontWeight: "400",
        fill: 0xffffff,
        align: "left",
      }),
    [viewportConfig, finalSectionFontSize]
  );

  // Layout mode: two-column side-by-side for mobile landscape, stacked for others
  const useTwoColumnCheckboxLayout = useMemo(
    () =>
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE,
    [device, orientation]
  );

  // Gap between columns in two-column layout (mobile landscape)
  const twoColumnGap = useMemo(() => {
    if (!viewportConfig || !useTwoColumnCheckboxLayout) return 0;
    return viewportConfig.sectionGap * 3.25;
  }, [viewportConfig, useTwoColumnCheckboxLayout]);

  /**
   * Calculate button grid configuration
   * - 5 columns for tablet landscape/desktop, 3 columns for others
   * - Mobile landscape: buttons use 45% width (checkboxes use 55%)
   * - Buttons maintain natural aspect ratio from texture
   */
  const buttonConfig = useMemo(() => {
    if (!viewportConfig)
      return {
        width: 100,
        height: 50,
        fontSize: 18,
        spacing: 10,
        rowSpacing: 10,
        columns: 3,
      };

    const columns =
      (device === PixiSettings.DEVICE.TABLET &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE) ||
      device === PixiSettings.DEVICE.DESKTOP
        ? 5
        : 3;
    const gapRatio = 0.075;

    // Mobile landscape: buttons get 45% width, other layouts use full width
    const availableWidth = useTwoColumnCheckboxLayout
      ? (viewportConfig.maxWidth - twoColumnGap) * 0.45
      : viewportConfig.maxWidth;

    // Calculate button dimensions maintaining natural aspect ratio
    const buttonWidth = availableWidth / (columns + (columns - 1) * gapRatio);
    const buttonHeight = buttonWidth / buttonAspectRatio;
    const spacing = buttonWidth * gapRatio;
    const rowSpacing = spacing;

    return {
      width: buttonWidth,
      height: buttonHeight,
      fontSize: Math.round(buttonHeight * 0.5),
      spacing,
      rowSpacing,
      columns,
    };
  }, [
    viewportConfig,
    buttonAspectRatio,
    device,
    orientation,
    useTwoColumnCheckboxLayout,
    twoColumnGap,
  ]);

  // Fade in/out animation when popup opens/closes
  useEffect(() => {
    if (containerRef.current) {
      if (popupActive) {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          easing: "easeOutQuad",
        });
      } else {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 0,
          duration: 200,
          easing: "easeInQuad",
        });
      }
    }
  }, [popupActive]);

  // Clear focused input when popup closes
  useEffect(() => {
    if (!popupActive && focusedInput !== null) {
      setFocusedInput(null);
    }
  }, [popupActive, focusedInput]);

  // Get responsive button config for close button sizing
  const closeButtonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  /**
   * Calculate popup container position
   * Windowed mode: Positioned above toolbar with configurable offsets
   * Fullscreen mode: Centered on screen
   */
  const popupContainerPosition = useMemo(() => {
    if (showPopupFrame && toolbar && toolbar.width && popupSize.width) {
      const config = POPUP_CONFIG[device]?.[orientation] || {
        positionOffsetRight: 1.25,
        positionOffsetBottom: 2.25,
      };

      const offsetFromBottom =
        popupSize.height * 0.5 +
        closeButtonConfig.height * config.positionOffsetBottom;

      return {
        x: dimensions.width / 2 + toolbar.width / 5.5,
        y: dimensions.height - offsetFromBottom,
      };
    }
    return {
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    };
  }, [
    showPopupFrame,
    dimensions,
    toolbar,
    popupSize,
    closeButtonConfig,
    device,
    orientation,
  ]);

  /**
   * Calculate close button position
   * Aligned with title at right edge of content area
   * Accounts for button anchor (0.5) and viewport padding
   */
  const closeButtonPosition = useMemo(() => {
    if (!viewportConfig) {
      return { x: 0, y: 0 };
    }

    const buttonSize = closeButtonConfig.width * (showPopupFrame ? 0.65 : 0.85);
    const relativeX = viewportConfig.maxWidth - buttonSize / 2;
    const relativeY = viewportConfig.titleFontSize / 2;

    return {
      x: popupContainerPosition.x + viewportConfig.x + relativeX,
      y: popupContainerPosition.y + viewportConfig.y + relativeY,
    };
  }, [
    viewportConfig,
    popupContainerPosition,
    closeButtonConfig,
    showPopupFrame,
  ]);

  /**
   * Calculate checkbox and input field configuration
   * Checkbox size based on close button, inputs maintain 2.5:1 aspect ratio
   */
  const checkboxConfig = useMemo(() => {
    if (!viewportConfig)
      return {
        size: 0,
        labelFontSize: 18,
        spacing: 30,
        xOffset: 0,
        inputWidth: 0,
        inputHeight: 0,
      };

    const checkboxSize = closeButtonConfig.width * 0.7;
    const labelFontSize = Math.round(viewportConfig.sectionFontSize * 0.75);
    const spacing = checkboxSize * 1.5;
    const xOffset = checkboxSize / 2; // Account for checkbox anchor 0.5

    const inputHeight = checkboxSize * 1.0;
    const inputWidth = inputHeight * 2.25; // Reduced from 2.5 to 2.0 for better spacing

    return {
      size: checkboxSize,
      labelFontSize,
      spacing,
      xOffset,
      inputWidth,
      inputHeight,
    };
  }, [viewportConfig, closeButtonConfig]);

  // Mode toggle button configuration (Basic/Advanced)
  // Slightly bigger than input fields with more text padding and triangle indicator
  const modeButtonConfig = useMemo(() => {
    if (!showPopupFrame || !checkboxConfig) return null;

    // Make button bigger: 1.2x height and 1.6x width (expanded for triangle)
    const buttonHeight = checkboxConfig.inputHeight * 1.2;
    const buttonWidth = checkboxConfig.inputWidth * 1.6;

    // Triangle configuration
    const triangleSize = buttonHeight * 0.2; // Triangle size relative to button height
    const triangleGap = buttonHeight * 0.1; // Small gap between text and triangle

    // Auto-scale font size based on text width
    // Reduced font size to create bigger gap between text and triangle
    const longerText = advancedText; // Use longer text for calculation
    const idealFontSize = Math.round(buttonHeight * 0.5);
    // Calculate available width: button width minus padding, triangle space, and gap
    const buttonPadding = buttonWidth * 0.1; // 10% padding on each side
    const textAreaWidth =
      buttonWidth - buttonPadding * 2 - triangleSize - triangleGap;
    const maxTextWidth = textAreaWidth * 0.95; // 95% of available text area (less aggressive)

    // Measure text width with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "400",
    });
    const metrics = PIXI.TextMetrics.measureText(longerText, tempStyle);

    // Scale down if text exceeds max width
    let fontSize = idealFontSize;
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      fontSize = Math.round(idealFontSize * scale);
    }

    // Calculate text X position: center of the text area (left side of button)
    const textAreaCenterX =
      -buttonWidth / 2 + buttonPadding + textAreaWidth / 2;

    return {
      width: buttonWidth,
      height: buttonHeight,
      fontSize,
      triangleSize,
      textX: textAreaCenterX,
      style: new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize,
        fontWeight: "400",
        fill: 0xffffff,
        align: "center",
      }),
    };
  }, [showPopupFrame, checkboxConfig, advancedText]);

  /**
   * Calculate Y position for Numbers of Rounds section
   * Mobile landscape: Positioned at bottom (side-by-side with checkboxes)
   * Windowed mode: Fixed position below Stop Autospin section (same in Basic/Advanced)
   * Other layouts: Positioned below Stop Autospin section
   */
  const numbersOfRoundsSectionY = useMemo(() => {
    if (!viewportConfig || !sectionHeadingStyle || !checkboxConfig) return 0;

    if (useTwoColumnCheckboxLayout) {
      // Mobile landscape: calculate from bottom
      const sectionHeadingHeight = sectionHeadingStyle.fontSize * 1.2;
      const numberOfRows = Math.ceil(
        AUTOPLAY_NUMBERS.length / buttonConfig.columns
      );
      const buttonsGridHeight =
        numberOfRows * buttonConfig.height +
        (numberOfRows - 1) * buttonConfig.rowSpacing +
        buttonConfig.height;

      const totalSectionHeight = sectionHeadingHeight + buttonsGridHeight;
      return viewportConfig.viewportHeight - totalSectionHeight;
    }

    // Stacked layout
    if (showPopupFrame) {
      // In Advanced mode: position below Stop Autospin section
      if (isAdvancedMode.current) {
        const stopAutospinTitleHeight = viewportConfig.sectionFontSize;
        const stopAutospinGap = viewportConfig.sectionGap * 2.6;
        const checkboxesHeight =
          checkboxConfig.spacing * 4 + checkboxConfig.size;
        const gapBetweenSections = viewportConfig.sectionGap * 2;

        return (
          stopAutospinTitleHeight +
          stopAutospinGap +
          checkboxesHeight +
          gapBetweenSections
        );
      }

      // In Basic mode: position near the top with small offset
      return viewportConfig.sectionGap * 2;
    }

    // Fullscreen mode: position below Stop Autospin section
    const stopAutospinTitleHeight = viewportConfig.sectionFontSize;
    const stopAutospinGap = viewportConfig.sectionGap * 2.5;
    const checkboxesHeight = checkboxConfig.spacing * 4 + checkboxConfig.size;
    const gapBetweenSections = viewportConfig.sectionGap * 2;

    return (
      stopAutospinTitleHeight +
      stopAutospinGap +
      checkboxesHeight +
      gapBetweenSections
    );
  }, [
    viewportConfig,
    sectionHeadingStyle,
    buttonConfig,
    checkboxConfig,
    useTwoColumnCheckboxLayout,
    showPopupFrame,
  ]);

  // Calculate if buttons should be disabled (guard conditions)
  const buttonsDisabled = useMemo(() => {
    const hasWin = currentRound?.win && currentRound.win > 0;
    const winNotFullyDisplayed =
      hasWin && spinComplete && !counterAnimationFinished;

    return isSpinning || criticalAnimationPlaying || winNotFullyDisplayed;
  }, [
    isSpinning,
    criticalAnimationPlaying,
    currentRound,
    spinComplete,
    counterAnimationFinished,
  ]);

  /**
   * Calculate grid positions for autoplay number buttons
   * Windowed mode & mobile landscape: left-aligned
   * Other fullscreen modes: centered
   */
  const buttonPositions = useMemo(() => {
    if (!viewportConfig || !sectionHeadingStyle) return [];

    const verticalPadding = buttonConfig.height / 2 + 10;
    const positions = [];

    for (let i = 0; i < AUTOPLAY_NUMBERS.length; i++) {
      const row = Math.floor(i / buttonConfig.columns);
      const col = i % buttonConfig.columns;

      let x;
      if (showPopupFrame || useTwoColumnCheckboxLayout) {
        // Left-aligned for windowed mode and mobile landscape
        x =
          buttonConfig.width / 2 +
          col * (buttonConfig.width + buttonConfig.spacing);
      } else {
        // Centered for other fullscreen modes
        x =
          -(
            (buttonConfig.columns - 1) *
            (buttonConfig.width + buttonConfig.spacing)
          ) /
            2 +
          col * (buttonConfig.width + buttonConfig.spacing);
      }

      const y =
        verticalPadding + row * (buttonConfig.height + buttonConfig.rowSpacing);

      positions.push({ x: Math.round(x), y: Math.round(y) });
    }

    return positions;
  }, [
    buttonConfig,
    showPopupFrame,
    sectionHeadingStyle,
    viewportConfig,
    useTwoColumnCheckboxLayout,
  ]);

  /**
   * Handle autoplay number selection
   * Validates balance before starting autoplay, then closes popup
   */
  const handleNumberSelect = useCallback(
    (number) => {
      // Prevent starting new spin if ongoing spin, win lines displaying, or critical animation
      const hasWin = currentRound?.win && currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin && spinComplete && !counterAnimationFinished;

      if (isSpinning || winNotFullyDisplayed || criticalAnimationPlaying) {
        return;
      }

      // Allow autoplay start regardless of balance - API will handle insufficient credits
      soundManager.play(SOUND_NAME.button_click);

      dispatch(setAutoSpinCount(number));
      dispatch(startAutoPlay());
      dispatch(closeAutoplayPopup());
    },
    [
      dispatch,
      isSpinning,
      criticalAnimationPlaying,
      currentRound,
      spinComplete,
      counterAnimationFinished,
    ]
  );

  if (!popupFrameTexture || !viewportConfig || !titleStyle) {
    return null;
  }

  return (
    <Container
      ref={containerRef}
      name="secondary_autoplay_overlay"
      zIndex={10}
      sortableChildren={true}
      eventMode={popupActive ? "static" : "none"}
    >
      {/* Invisible clickable background for windowed mode - closes popup when clicked */}
      {showPopupFrame && (
        <Graphics
          name="secondary_autoplay_click_outside"
          zIndex={0}
          draw={(g) => {
            g.clear();
            g.beginFill(0x000000, 0.001); // Nearly transparent but still interactive
            g.drawRect(0, 0, dimensions.width, dimensions.height);
            g.endFill();
          }}
          eventMode={popupActive ? "static" : "none"}
          onpointertap={handleClose}
        />
      )}

      {/* Gray Background - only for fullscreen (mobile/tablet portrait) */}
      {/* Note: Background click to close is disabled for mobile/tablet portrait - only enabled for desktop/tablet landscape */}
      {!showPopupFrame && (
        <Graphics
          name="secondary_autoplay_background"
          zIndex={0}
          draw={(g) => {
            g.clear();
            g.beginFill(0x161616, 1);
            g.drawRect(0, 0, dimensions.width, dimensions.height);
            g.endFill();
          }}
          eventMode="none"
        />
      )}

      {/* Popup Container */}
      <Container
        name="secondary_autoplay_popup"
        x={popupContainerPosition.x}
        y={popupContainerPosition.y}
        zIndex={1}
      >
        {/* Popup Frame Sprite (Tablet Landscape/Desktop only) */}
        {showPopupFrame && (
          <Sprite
            texture={popupFrameTexture}
            anchor={0.5}
            width={popupSize.width}
            height={popupSize.height}
          />
        )}

        {/* Content Viewport Container */}
        <Container
          name="secondary_autoplay_content_viewport"
          x={viewportConfig.x}
          y={viewportConfig.y}
        >
          {/* Title */}
          <Text
            text={titleText}
            style={titleStyle}
            anchor={{ x: 0, y: 0 }}
            x={0}
            y={0}
            resolution={2}
          />

          {/* Basic/Advanced Mode Toggle Button (Windowed mode only) */}
          {showPopupFrame && modeButtonConfig && (
            <Container
              x={viewportConfig.maxWidth - modeButtonConfig.width / 2}
              y={viewportConfig.titleFontSize / 2}
              eventMode={buttonsDisabled ? "none" : "static"}
              cursor={buttonsDisabled ? "default" : "pointer"}
              onpointertap={handleModeToggle}
            >
              {/* Button Background */}
              <Sprite
                texture={buttonBackgroundTexture}
                anchor={0.5}
                width={modeButtonConfig.width}
                height={modeButtonConfig.height}
              />
              {/* Button Text */}
              <Text
                text={isAdvancedMode.current ? basicText : advancedText}
                style={modeButtonConfig.style}
                anchor={0.5}
                x={Math.round(modeButtonConfig.textX)}
                y={0}
                resolution={2}
              />
              {/* Triangle Indicator */}
              {triangleTexture &&
                (() => {
                  const triangleGap = modeButtonConfig.height * 0.15;

                  // Get actual displayed text (swapped: collapsed shows Advanced, expanded shows Basic)
                  const currentText = isAdvancedMode.current
                    ? basicText
                    : advancedText;

                  // Measure actual text width with current font size
                  const textMetrics = PIXI.TextMetrics.measureText(
                    currentText,
                    modeButtonConfig.style
                  );
                  const actualTextWidth = textMetrics.width;

                  // Calculate triangle size maintaining aspect ratio
                  const triangleHeight = modeButtonConfig.triangleSize;
                  const triangleAspectRatio =
                    triangleTexture.width / triangleTexture.height;
                  const triangleWidth = triangleHeight * triangleAspectRatio;

                  // Position triangle right after the text
                  const triangleX =
                    modeButtonConfig.textX +
                    actualTextWidth / 2 +
                    triangleGap +
                    triangleWidth / 2;

                  // Rotate: 0 when Basic/expanded (pointing up), Math.PI when Advanced/collapsed (pointing down)
                  const rotation = isAdvancedMode.current ? 0 : Math.PI;

                  return (
                    <Sprite
                      key={`triangle-${
                        isAdvancedMode.current ? "basic" : "advanced"
                      }`}
                      texture={triangleTexture}
                      anchor={0.5}
                      width={triangleWidth}
                      height={triangleHeight}
                      x={triangleX}
                      y={0}
                      rotation={rotation}
                    />
                  );
                })()}
            </Container>
          )}

          {useTwoColumnCheckboxLayout ? (
            /**
             * Mobile Landscape Layout: Two-column side-by-side
             * Left: Stop Autospin conditions (55% width)
             * Right: Numbers of Rounds buttons (45% width)
             */
            <>
              {/* Left Column - Stop Autospin Section (55% width) - Always shown in fullscreen */}
              <Container x={0} y={viewportConfig.titleYOffset}>
                <Text
                  text={stopAutospinText}
                  style={sectionHeadingStyle}
                  anchor={{ x: 0, y: 0 }}
                  x={0}
                  y={0}
                  resolution={2}
                />

                {/* Checkboxes Container */}
                <Container
                  y={
                    viewportConfig.sectionFontSize +
                    viewportConfig.sectionGap * 2.5
                  }
                >
                  <Checkbox
                    translationKey="ui_autospins_on_any_win"
                    checked={stopOnAnyWin}
                    onChange={(checked) => dispatch(setStopOnAnyWin(checked))}
                    x={checkboxConfig.xOffset}
                    y={0}
                    sizeMultiplier={0.5}
                    labelFontSize={checkboxConfig.labelFontSize}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) * 0.55 -
                      checkboxConfig.xOffset
                    }
                  />
                  <Checkbox
                    translationKey="ui_autospins_if_bonus_won"
                    checked={stopOnBonusGame}
                    onChange={(checked) => dispatch(setStopOnBonusGame(checked))}
                    x={checkboxConfig.xOffset}
                    y={checkboxConfig.spacing}
                    sizeMultiplier={0.5}
                    labelFontSize={checkboxConfig.labelFontSize}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) * 0.55 -
                      checkboxConfig.xOffset
                    }
                  />
                  <CheckboxWithInput
                    translationKey="ui_autospins_if_single_win"
                    checked={stopOnSingleWinExceeds}
                    onChange={(checked) => {
                      dispatch(setStopOnSingleWinExceeds(checked));
                      if (!checked) {
                        dispatch(setSingleWinExceedsAmount(""));
                      }
                    }}
                    value={singleWinExceedsValue}
                    onValueChange={(value) => {
                      dispatch(setSingleWinExceedsAmount(value));
                      // Auto-check/uncheck based on value (only when typing, not when checkbox toggles)
                      if (value && value !== "") {
                        dispatch(setStopOnSingleWinExceeds(true));
                      } else if (value === "" && stopOnSingleWinExceeds) {
                        dispatch(setStopOnSingleWinExceeds(false));
                      }
                    }}
                    x={checkboxConfig.xOffset}
                    y={checkboxConfig.spacing * 2}
                    sizeMultiplier={0.6}
                    labelFontSize={checkboxConfig.labelFontSize}
                    inputWidth={checkboxConfig.inputWidth}
                    inputHeight={checkboxConfig.inputHeight}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) * 0.55 -
                      checkboxConfig.xOffset
                    }
                    inputId="singleWinExceeds"
                    focusedInput={focusedInput}
                    onFocusChange={setFocusedInput}
                  />
                  <CheckboxWithInput
                    translationKey="ui_autospins_if_increases"
                    checked={stopOnBalanceIncreases}
                    onChange={(checked) => {
                      dispatch(setStopOnBalanceIncreases(checked));
                      if (!checked) {
                        dispatch(setBalanceIncreasesAmount(""));
                      }
                    }}
                    value={balanceIncreasesValue}
                    onValueChange={(value) => {
                      dispatch(setBalanceIncreasesAmount(value));
                      // Auto-check/uncheck based on value (only when typing, not when checkbox toggles)
                      if (value && value !== "") {
                        dispatch(setStopOnBalanceIncreases(true));
                      } else if (value === "" && stopOnBalanceIncreases) {
                        dispatch(setStopOnBalanceIncreases(false));
                      }
                    }}
                    x={checkboxConfig.xOffset}
                    y={checkboxConfig.spacing * 3}
                    sizeMultiplier={0.6}
                    labelFontSize={checkboxConfig.labelFontSize}
                    inputWidth={checkboxConfig.inputWidth}
                    inputHeight={checkboxConfig.inputHeight}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) * 0.55 -
                      checkboxConfig.xOffset
                    }
                    inputId="balanceIncreases"
                    focusedInput={focusedInput}
                    onFocusChange={setFocusedInput}
                  />
                  <CheckboxWithInput
                    translationKey="ui_autospins_if_decreases"
                    checked={stopOnBalanceDecreases}
                    onChange={(checked) => {
                      dispatch(setStopOnBalanceDecreases(checked));
                      if (!checked) {
                        dispatch(setBalanceDecreasesAmount(""));
                      }
                    }}
                    value={balanceDecreasesValue}
                    onValueChange={(value) => {
                      dispatch(setBalanceDecreasesAmount(value));
                      // Auto-check/uncheck based on value (only when typing, not when checkbox toggles)
                      if (value && value !== "") {
                        dispatch(setStopOnBalanceDecreases(true));
                      } else if (value === "" && stopOnBalanceDecreases) {
                        dispatch(setStopOnBalanceDecreases(false));
                      }
                    }}
                    x={checkboxConfig.xOffset}
                    y={checkboxConfig.spacing * 4}
                    sizeMultiplier={0.6}
                    labelFontSize={checkboxConfig.labelFontSize}
                    inputWidth={checkboxConfig.inputWidth}
                    inputHeight={checkboxConfig.inputHeight}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) * 0.55 -
                      checkboxConfig.xOffset
                    }
                    inputId="balanceDecreases"
                    focusedInput={focusedInput}
                    onFocusChange={setFocusedInput}
                  />
                </Container>
              </Container>

              {/* Right Column - Numbers of Rounds Section (45% width) */}
              <Container
                x={
                  (viewportConfig.maxWidth - twoColumnGap) * 0.55 + twoColumnGap
                }
                y={viewportConfig.titleYOffset}
              >
                <Text
                  text={numbersOfRoundsText}
                  style={sectionHeadingStyle}
                  anchor={{ x: 0, y: 0 }}
                  x={0}
                  y={0}
                  resolution={2}
                />

                {/* Autoplay Number Buttons Grid */}
                <Container
                  x={0}
                  y={viewportConfig.sectionFontSize + viewportConfig.sectionGap}
                >
                  {AUTOPLAY_NUMBERS.map((number, index) => {
                    const position = buttonPositions[index];
                    if (!position) return null;

                    const isDisabled = balance < bet || buttonsDisabled;
                    const displayText =
                      number === Infinity ? "∞" : number.toString();

                    return (
                      <BetButton
                        key={number}
                        x={position.x}
                        y={position.y}
                        text={displayText}
                        onClick={() => handleNumberSelect(number)}
                        disabled={isDisabled}
                        active={false}
                        width={buttonConfig.width}
                        height={buttonConfig.height}
                        fontSize={buttonConfig.fontSize}
                      />
                    );
                  })}
                </Container>
              </Container>
            </>
          ) : (
            /**
             * Stacked Layout: For all devices except mobile landscape
             * Stop Autospin section on top (only in Advanced mode for windowed), Numbers of Rounds section below
             */
            <>
              {/* Stop Autospin Section - conditionally rendered in windowed mode */}
              {(!showPopupFrame || isAdvancedMode.current) && (
                <Container y={viewportConfig.titleYOffset}>
                  <Text
                    text={stopAutospinText}
                    style={sectionHeadingStyle}
                    anchor={{ x: 0, y: 0 }}
                    x={0}
                    y={0}
                  />

                  {/* Checkboxes Container */}
                  <Container
                    y={
                      viewportConfig.sectionFontSize +
                      viewportConfig.sectionGap * 2.5
                    }
                  >
                    <Checkbox
                      translationKey="ui_autospins_on_any_win"
                      checked={stopOnAnyWin}
                      onChange={(checked) => dispatch(setStopOnAnyWin(checked))}
                      x={checkboxConfig.xOffset}
                      y={0}
                      sizeMultiplier={0.7}
                      labelFontSize={checkboxConfig.labelFontSize}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                    />
                    <Checkbox
                      translationKey="ui_autospins_if_bonus_won"
                      checked={stopOnBonusGame}
                      onChange={(checked) => dispatch(setStopOnBonusGame(checked))}
                      x={checkboxConfig.xOffset}
                      y={checkboxConfig.spacing}
                      sizeMultiplier={0.7}
                      labelFontSize={checkboxConfig.labelFontSize}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                    />
                    <CheckboxWithInput
                      translationKey="ui_autospins_if_single_win"
                      checked={stopOnSingleWinExceeds}
                      onChange={(checked) => {
                        dispatch(setStopOnSingleWinExceeds(checked));
                        if (!checked) {
                          dispatch(setSingleWinExceedsAmount(""));
                        }
                      }}
                      value={singleWinExceedsValue}
                      onValueChange={(value) => {
                        dispatch(setSingleWinExceedsAmount(value));
                        // Auto-check/uncheck based on value (only when typing, not when checkbox toggles)
                        if (value && value !== "") {
                          dispatch(setStopOnSingleWinExceeds(true));
                        } else if (value === "" && stopOnSingleWinExceeds) {
                          dispatch(setStopOnSingleWinExceeds(false));
                        }
                      }}
                      x={checkboxConfig.xOffset}
                      y={checkboxConfig.spacing * 2}
                      sizeMultiplier={0.7}
                      labelFontSize={checkboxConfig.labelFontSize}
                      inputWidth={checkboxConfig.inputWidth}
                      inputHeight={checkboxConfig.inputHeight}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                      inputId="singleWinExceeds"
                      focusedInput={focusedInput}
                      onFocusChange={setFocusedInput}
                    />
                    <CheckboxWithInput
                      translationKey="ui_autospins_if_increases"
                      checked={stopOnBalanceIncreases}
                      onChange={(checked) => {
                        dispatch(setStopOnBalanceIncreases(checked));
                        if (!checked) {
                          dispatch(setBalanceIncreasesAmount(""));
                        }
                      }}
                      value={balanceIncreasesValue}
                      onValueChange={(value) => {
                        dispatch(setBalanceIncreasesAmount(value));
                        // Auto-check/uncheck based on value (only when typing, not when checkbox toggles)
                        if (value && value !== "") {
                          dispatch(setStopOnBalanceIncreases(true));
                        } else if (value === "" && stopOnBalanceIncreases) {
                          dispatch(setStopOnBalanceIncreases(false));
                        }
                      }}
                      x={checkboxConfig.xOffset}
                      y={checkboxConfig.spacing * 3}
                      sizeMultiplier={0.7}
                      labelFontSize={checkboxConfig.labelFontSize}
                      inputWidth={checkboxConfig.inputWidth}
                      inputHeight={checkboxConfig.inputHeight}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                      inputId="balanceIncreases"
                      focusedInput={focusedInput}
                      onFocusChange={setFocusedInput}
                    />
                    <CheckboxWithInput
                      translationKey="ui_autospins_if_decreases"
                      checked={stopOnBalanceDecreases}
                      onChange={(checked) => {
                        dispatch(setStopOnBalanceDecreases(checked));
                        if (!checked) {
                          dispatch(setBalanceDecreasesAmount(""));
                        }
                      }}
                      value={balanceDecreasesValue}
                      onValueChange={(value) => {
                        dispatch(setBalanceDecreasesAmount(value));
                        // Auto-check/uncheck based on value (only when typing, not when checkbox toggles)
                        if (value && value !== "") {
                          dispatch(setStopOnBalanceDecreases(true));
                        } else if (value === "" && stopOnBalanceDecreases) {
                          dispatch(setStopOnBalanceDecreases(false));
                        }
                      }}
                      x={checkboxConfig.xOffset}
                      y={checkboxConfig.spacing * 4}
                      sizeMultiplier={0.7}
                      labelFontSize={checkboxConfig.labelFontSize}
                      inputWidth={checkboxConfig.inputWidth}
                      inputHeight={checkboxConfig.inputHeight}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                      inputId="balanceDecreases"
                      focusedInput={focusedInput}
                      onFocusChange={setFocusedInput}
                    />
                  </Container>
                </Container>
              )}

              {/* Numbers of Rounds Section */}
              <Container
                key={`numbers-section-${useTwoColumnCheckboxLayout}`}
                y={viewportConfig.titleYOffset + numbersOfRoundsSectionY}
              >
                <Text
                  text={numbersOfRoundsText}
                  style={sectionHeadingStyle}
                  anchor={{ x: 0, y: 0 }}
                  x={0}
                  y={0}
                  resolution={2}
                />

                {/* Autoplay Number Buttons Grid */}
                <Container
                  key={`buttons-grid-${useTwoColumnCheckboxLayout}`}
                  x={showPopupFrame ? 0 : viewportConfig.maxWidth / 2}
                  y={viewportConfig.sectionFontSize + viewportConfig.sectionGap}
                >
                  {AUTOPLAY_NUMBERS.map((number, index) => {
                    const position = buttonPositions[index];
                    if (!position) return null;

                    const isDisabled = balance < bet || buttonsDisabled;
                    const displayText =
                      number === Infinity ? "∞" : number.toString();

                    return (
                      <BetButton
                        key={number}
                        x={position.x}
                        y={position.y}
                        text={displayText}
                        onClick={() => handleNumberSelect(number)}
                        disabled={isDisabled}
                        active={false}
                        width={buttonConfig.width}
                        height={buttonConfig.height}
                        fontSize={buttonConfig.fontSize}
                      />
                    );
                  })}
                </Container>
              </Container>
            </>
          )}
        </Container>
      </Container>

      {/* Close Button - only show in fullscreen mode */}
      {!showPopupFrame && (
        <Container
          name="secondary_autoplay_close_button_container"
          x={closeButtonPosition.x}
          y={closeButtonPosition.y}
          zIndex={2}
        >
          <ButtonMenuClose
            onClose={handleClose}
            x={0}
            y={0}
            isSettings={true}
            sizeMultiplier={0.85}
          />
        </Container>
      )}
    </Container>
  );
};

export default AutoplayPopup;
